import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import AdminLogin    from './AdminLogin';
import GalleryManager  from './GalleryManager';
import ReviewsManager  from './ReviewsManager';
import HeroManager  from './HeroManager';
import YoutubeManager  from './YoutubeManager';
import {
  FiImage, FiMessageSquare, FiYoutube,
  FiLogOut, FiMenu, FiX, FiGrid,
  FiChevronRight
} from 'react-icons/fi';

const injectKeyframes = () => {
  if (document.getElementById('admin-kf')) return;
  const style = document.createElement('style');
  style.id = 'admin-kf';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    @keyframes spin   { to { transform: rotate(360deg); } }
    @keyframes fadein { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    @keyframes float  {
      0%,100% { transform: translateY(0) scale(1); }
      50%     { transform: translateY(-20px) scale(1.05); }
    }
    * { box-sizing: border-box; }
    body { margin: 0; }
    ::-webkit-scrollbar       { width: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(124,58,237,0.4); border-radius: 99px; }
  `;
  document.head.appendChild(style);
};

const TABS = [
  { key: 'hero',  label: 'Hero',  Icon: FiImage,        color: '#7c3aed' },
  { key: 'gallery',  label: 'Gallery',  Icon: FiImage,        color: '#7c3aed' },
  { key: 'reviews',  label: 'Reviews',  Icon: FiMessageSquare, color: '#059669' },
  { key: 'youtube',  label: 'YouTube',  Icon: FiYoutube,       color: '#dc2626' },
];

const AdminDashboard = () => {
  const [user, setUser]       = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab]         = useState('gallery');
  const [sideOpen, setSideOpen] = useState(true);

  injectKeyframes();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setChecking(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, sess) => {
      setUser(sess?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  if (checking) return (
    <div style={styles.fullCenter}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <img src="/assets/img/logo.png" alt="Logo" style={{ width: '80px', height: '80px', animation: 'float 3s ease-in-out infinite' }} />
        <span style={styles.bigSpinner} />
      </div>
    </div>
  );

  if (!user) return <AdminLogin onLogin={setUser} />;

  const activeTab = TABS.find(t => t.key === tab);

  return (
    <div style={styles.shell}>
      <aside style={{ ...styles.sidebar, ...(sideOpen ? {} : styles.sidebarClosed) }}>
        <div style={styles.brand}>
          <div style={styles.brandLogo}>
            <img src="/assets/img/logo.png" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '10px', backgroundColor: 'black' }} />
          </div>
          {sideOpen && (
            <div>
              <div style={styles.brandName}>Baby Twinkles</div>
              <div style={styles.brandSub}>Photography</div>
            </div>
          )}
        </div>

        <nav style={styles.nav}>
          {sideOpen && <div style={styles.navLabel}>MANAGEMENT</div>}
          {TABS.map(({ key, label, Icon, color }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                style={{
                  ...styles.navItem,
                  ...(active ? styles.navItemActive : {}),
                }}
              >
                <Icon
                  size={18}
                  color={active ? '#fff' : 'rgba(255,255,255,0.45)'}
                  style={{ flexShrink: 0 }}
                />
                {sideOpen && (
                  <>
                    <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
                    {active && <FiChevronRight size={14} />}
                  </>
                )}
                {active && <div style={{ ...styles.activePip, background: color }} />}
              </button>
            );
          })}
        </nav>

        {sideOpen && (
          <div style={styles.userBox}>
            <div style={styles.userAvatar}>
              {user.email?.[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.userEmail}>{user.email}</div>
              <div style={styles.userRole}>Administrator</div>
            </div>
            <button onClick={handleLogout} style={styles.logoutBtn} title="Sign out">
              <FiLogOut size={15} />
            </button>
          </div>
        )}
      </aside>

      <div style={styles.main}>
        <header style={styles.topbar}>
          <button style={styles.menuBtn} onClick={() => setSideOpen(p => !p)}>
            {sideOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>
          <div style={styles.breadcrumb}>
            <FiGrid size={14} style={{ opacity: 0.4 }} />
            <span style={{ opacity: 0.4 }}>Dashboard</span>
            <span style={{ opacity: 0.3 }}>/</span>
            <span style={{ color: activeTab?.color, fontWeight: 600 }}>{activeTab?.label}</span>
          </div>
          {!sideOpen && (
            <button onClick={handleLogout} style={styles.topLogout}>
              <FiLogOut size={15} /> Sign Out
            </button>
          )}
        </header>

        <main style={styles.content}>
          <div style={styles.contentInner}>
            {tab === 'hero'  && <HeroManager />}
            {tab === 'gallery'  && <GalleryManager />}
            {tab === 'reviews'  && <ReviewsManager />}
            {tab === 'youtube'  && <YoutubeManager />}
          </div>
        </main>
      </div>
    </div>
  );
};

const SIDE_W  = 240;
const SIDE_CW = 68;

const styles = {
  fullCenter: {
    minHeight: '100vh', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: '#0f0c29',
  },
  bigSpinner: {
    width: 48, height: 48,
    border: '4px solid rgba(255,255,255,0.1)',
    borderTopColor: '#7c3aed',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    display: 'inline-block',
  },
  shell: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    background: '#0e0b20',
    color: '#e8e6f0',
  },

  /* Sidebar */
  sidebar: {
    width: SIDE_W,
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #13102a 0%, #0e0b1f 100%)',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    transition: 'width 0.25s ease',
    overflow: 'hidden',
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  sidebarClosed: { width: SIDE_CW },

  brand: {
    padding: '24px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    flexShrink: 0,
  },
  brandLogo: {
    width: 38, height: 38, borderRadius: 10,
    background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 16px rgba(124,58,237,0.4)',
  },
  brandName: { fontSize: 15, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' },
  brandSub:  { fontSize: 11, opacity: 0.4, whiteSpace: 'nowrap' },

  nav: { flex: 1, padding: '16px 10px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' },
  navLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 1.5, opacity: 0.3, padding: '4px 8px 10px', textTransform: 'uppercase' },
  navItem: {
    width: '100%', border: 'none',
    background: 'transparent',
    color: 'rgba(255,255,255,0.45)',
    borderRadius: 10, padding: '11px 12px',
    cursor: 'pointer', fontSize: 14, fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: 12,
    transition: 'background 0.15s, color 0.15s',
    position: 'relative', whiteSpace: 'nowrap',
    textDecoration: 'none',
  },
  navItemActive: {
    background: 'linear-gradient(135deg, rgba(124,58,237,0.3), rgba(236,72,153,0.15))',
    color: '#fff',
    border: '1px solid rgba(124,58,237,0.25)',
  },
  activePip: {
    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
    width: 6, height: 6, borderRadius: '50%',
  },

  userBox: {
    padding: '14px 16px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', alignItems: 'center', gap: 10,
    flexShrink: 0,
  },
  userAvatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 14, flexShrink: 0,
  },
  userEmail: { fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole:  { fontSize: 11, opacity: 0.4 },
  logoutBtn: {
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
    color: '#ef4444', borderRadius: 8, padding: '6px 8px',
    cursor: 'pointer', flexShrink: 0, display: 'flex',
  },

  /* Main */
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },

  topbar: {
    height: 60,
    background: 'rgba(255,255,255,0.02)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '0 24px',
    position: 'sticky', top: 0, zIndex: 50,
    flexShrink: 0,
  },
  menuBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'inherit', borderRadius: 8,
    width: 36, height: 36, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  breadcrumb: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 14, flex: 1,
  },
  topLogout: {
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
    color: '#ef4444', borderRadius: 8, padding: '6px 12px',
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: 6,
  },

  content: { flex: 1, overflowY: 'auto', padding: '32px 28px' },
  contentInner: {
    maxWidth: 1200, margin: '0 auto',
    animation: 'fadein 0.3s ease',
  },
};

export default AdminDashboard;
